/**
 * HumanReviewChecklist Component
 *
 * Checklist for reviewer sign-off before completing a review.
 * All items must be checked before the review can be finalized.
 */

import { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';

interface HumanReviewChecklistProps {
  onComplete: (allChecked: boolean) => void;
  disabled?: boolean;
}

interface ChecklistItem {
  id: string;
  label: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'uncertain-review',
    label: 'I have reviewed all criteria marked as uncertain or low confidence',
  },
  {
    id: 'exclusion-verification',
    label: 'I have verified all exclusion criteria findings against source documents',
  },
  {
    id: 'flagged-items',
    label: 'I have checked flagged items and contradictions',
  },
  {
    id: 'overall-assessment',
    label: 'I confirm my overall eligibility assessment',
  },
];

export function HumanReviewChecklist({ onComplete, disabled = false }: HumanReviewChecklistProps) {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() =>
    CHECKLIST_ITEMS.reduce(
      (acc, item) => {
        acc[item.id] = false;
        return acc;
      },
      {} as Record<string, boolean>
    )
  );

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const allChecked = checkedCount === totalCount;

  // Notify parent component when completion status changes
  useEffect(() => {
    onComplete(allChecked);
  }, [allChecked, onComplete]);

  const handleToggle = (id: string) => {
    if (disabled) return;

    setCheckedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className={`space-y-4 ${disabled ? 'opacity-50' : ''}`}>
      {/* Progress indicator */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Review Checklist</span>
        <div className="flex items-center gap-2">
          {allChecked && <CheckCircle size={20} className="text-green-500 dark:text-green-400" />}
          <span
            className={`text-sm font-medium ${
              allChecked ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {checkedCount} of {totalCount} complete
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            allChecked ? 'bg-green-500' : 'bg-primary-600'
          }`}
          style={{ width: `${(checkedCount / totalCount) * 100}%` }}
        />
      </div>

      {/* Checklist items */}
      <div className="space-y-3">
        {CHECKLIST_ITEMS.map(item => {
          const isChecked = checkedItems[item.id];
          return (
            <div
              key={item.id}
              className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                isChecked
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => handleToggle(item.id)}
            >
              <input
                type="checkbox"
                id={item.id}
                checked={isChecked}
                onChange={() => handleToggle(item.id)}
                disabled={disabled}
                className={`h-5 w-5 mt-0.5 text-primary-600 dark:text-primary-400 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 ${
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              />
              <label
                htmlFor={item.id}
                className={`text-sm ${
                  isChecked ? 'text-green-800 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'
                } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} select-none flex-1`}
              >
                {item.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
